/**
 * Transactions Page - ZERO LAG IMPLEMENTATION
 * 
 * Performance Strategy:
 * 1. Always show skeleton first (instant feedback)
 * 2. Defer heavy rendering with requestIdleCallback
 * 3. Limit initial items (pagination)
 * 4. Progressive enhancement
 */

import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Stack, Container, SelectedCompaniesIndicator } from '../../components/primitives';
import { PageHeader } from '../../components/layout';
import { Card } from '../../components/ui/card';
import { useResponsive } from '../../hooks/useResponsive';
import { useCompanyContext } from '../../contexts/CompanyContext';
import { usePermissions } from '../../hooks/usePermissions';
import { PermissionGuard } from '../../components/permissions/PermissionGuard';
import { supabase } from '../../lib/supabase';

import { Receipt, Plus, Zap, Download, Repeat, Trash2, CheckSquare, Upload, ArrowUpDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../../components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { useTransactions } from './hooks/useTransactions';
import { useReconciledTransactions } from './hooks/useReconciledTransactions';
import { TransactionListItem } from './components/TransactionListItemMobile';
import { TransactionFilters, type TransactionFilterValues } from './components/TransactionFilters';
import { TransactionStats } from './components/TransactionStats';
import { TransactionQuickActions } from './components/TransactionQuickActions';
import { TransactionEmptyState } from './components/TransactionEmptyState';
import { TransactionListSkeleton } from './components/TransactionListSkeleton';
import { RecurringTransactionsManager } from './components/RecurringTransactionsManager';
import { DeleteRecurrenceDialog, type DeleteOption } from './components/DeleteRecurrenceDialog';
import { EditRecurrenceDialog, type EditOption } from './components/EditRecurrenceDialog';
import { ExportDialog } from './components/ExportDialog';
import { ImportDialog } from './components/ImportDialog';
import type { CashFlowEntry } from '../../types/database';
import { toast } from 'sonner';
import { cn } from '../../components/ui/utils';

interface TransactionsPageProps {
  userId: string;
  onNavigateToForm: (
    transactionId?: string, 
    options?: { mode?: 'recurring' | 'duplicate'; duplicateFromId?: string }
  ) => void;
}

export function TransactionsPage({ userId, onNavigateToForm }: TransactionsPageProps) {
  const { selectedCompanyIds, selectedCompanies, hasMultipleSelected } = useCompanyContext();
  const { transactions, isLoading, isError, error, deleteTransaction, deleteRecurringTransaction, isDeleting } = useTransactions({ companyIds: selectedCompanyIds, userId });
  const { reconciledIds, isLoadingReconciled } = useReconciledTransactions(selectedCompanyIds);
  const { canCreate, canEdit, canDelete } = usePermissions();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [recurringManagerOpen, setRecurringManagerOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [recurringDeleteDialog, setRecurringDeleteDialog] = useState<{
    open: boolean;
    transaction: CashFlowEntry | null;
  }>({ open: false, transaction: null });
  const [recurringEditDialog, setRecurringEditDialog] = useState<{
    open: boolean;
    transaction: CashFlowEntry | null;
  }>({ open: false, transaction: null });
  const { isMobile } = useResponsive();
  
  // PERFORMANCE: Show skeleton on mount, render content after paint
  const [showContent, setShowContent] = useState(false);
  
  // Filters state
  const [filters, setFilters] = useState<TransactionFilterValues>({
    search: '',
    entryType: 'all',
    paymentStatus: 'all',
    viewMode: 'all',
  });

  // Sort state
  type SortOption = 'date-desc' | 'date-asc' | 'amount-desc' | 'amount-asc' | 'description-asc' | 'description-desc';
  const [sortBy, setSortBy] = useState<SortOption>('date-desc');

  // Pagination for better performance
  const [visibleCount, setVisibleCount] = useState(20);

  // Helper: Get the correct date to use for a transaction (same logic as Scenarios)
  const getEffectiveDate = (transaction: CashFlowEntry): Date => {
    // If confirmed/paid, use payment_date
    if (transaction.payment_status === 'confirmado' && transaction.payment_date) {
      return new Date(transaction.payment_date);
    }
    
    // If pending/scheduled, use due_date
    if (transaction.payment_status === 'previsão' && transaction.due_date) {
      return new Date(transaction.due_date);
    }
    
    // Fallback to entry_date
    return new Date(transaction.entry_date || '');
  };

  // Get unique categories for filter
  const categories = useMemo(() => {
    if (!showContent) return [];
    return Array.from(new Set(transactions.map(t => t.category).filter(Boolean)));
  }, [transactions, showContent]);

  // Fetch bank accounts for filtering
  const { data: bankAccounts = [] } = useQuery({
    queryKey: ['bank-accounts-for-filter', selectedCompanyIds],
    queryFn: async () => {
      if (!selectedCompanyIds || selectedCompanyIds.length === 0) return [];
      
      const { data, error } = await supabase
        .from('bank_accounts')
        .select('id, account_name')
        .in('company_id', selectedCompanyIds)
        .eq('is_active', true)
        .order('account_name');

      if (error) {
        console.error('[TransactionsPage] Error fetching bank accounts:', error);
        return [];
      }
      
      return data || [];
    },
    enabled: selectedCompanyIds.length > 0,
  });

  // Filter and sort transactions
  const filteredTransactions = useMemo(() => {
    if (!showContent) return [];
    
    let filtered = transactions.filter(transaction => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch = 
          transaction.description.toLowerCase().includes(searchLower) ||
          transaction.category?.toLowerCase().includes(searchLower) ||
          transaction.business_partner?.toLowerCase().includes(searchLower) ||
          String(transaction.amount).includes(searchLower);
        
        if (!matchesSearch) return false;
      }

      // Entry type filter
      if (filters.entryType !== 'all' && transaction.entry_type !== filters.entryType) {
        return false;
      }

      // Payment status filter
      if (filters.paymentStatus !== 'all' && transaction.payment_status !== filters.paymentStatus) {
        return false;
      }

      // Date filter - FIXED: Use getEffectiveDate() to match Scenarios logic
      // This respects payment_status: confirmed uses payment_date, pending uses due_date
      const relevantDate = getEffectiveDate(transaction);
      
      // If custom date range is set, use it (takes precedence)
      if (filters.dateFrom || filters.dateTo) {
        if (filters.dateFrom) {
          // Set time to start of day for dateFrom
          const startOfDay = new Date(filters.dateFrom);
          startOfDay.setHours(0, 0, 0, 0);
          if (relevantDate < startOfDay) return false;
        }
        if (filters.dateTo) {
          // Set time to end of day for dateTo
          const endOfDay = new Date(filters.dateTo);
          endOfDay.setHours(23, 59, 59, 999);
          if (relevantDate > endOfDay) return false;
        }
      } 
      // Otherwise, apply viewMode filter
      else if (filters.viewMode && filters.viewMode !== 'all') {
        const now = new Date();
        
        if (filters.viewMode === 'monthly') {
          // Filter by current month
          const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
          const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          if (relevantDate < firstDayOfMonth || relevantDate > lastDayOfMonth) return false;
        } else if (filters.viewMode === 'ytd') {
          // Year to Date - do início do ano até hoje
          const firstDayOfYear = new Date(now.getFullYear(), 0, 1);
          const today = new Date();
          today.setHours(23, 59, 59, 999); // fim do dia de hoje
          if (relevantDate < firstDayOfYear || relevantDate > today) return false;
        } else if (filters.viewMode === 'yearly') {
          // Filter by current year
          const firstDayOfYear = new Date(now.getFullYear(), 0, 1);
          const lastDayOfYear = new Date(now.getFullYear(), 11, 31);
          if (relevantDate < firstDayOfYear || relevantDate > lastDayOfYear) return false;
        } else if (filters.viewMode === 'all-time') {
          // Todo o período - sem filtro de data
          // Não faz nada, mostra tudo
        }
        // 'all' = no date filter, show all
      }

      // Category filter
      if (filters.category && transaction.category !== filters.category) {
        return false;
      }

      // Amount range filter
      const amount = Number(transaction.amount);
      if (filters.minAmount && amount < filters.minAmount) return false;
      if (filters.maxAmount && amount > filters.maxAmount) return false;

      // Bank account filter
      if (filters.bankAccountId && transaction.bank_account_id !== filters.bankAccountId) {
        return false;
      }

      return true;
    });

    // Apply sorting
    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          // Data mais recente primeiro (usa getEffectiveDate)
          const dateA = getEffectiveDate(a);
          const dateB = getEffectiveDate(b);
          return dateB.getTime() - dateA.getTime();
        
        case 'date-asc':
          // Data mais antiga primeiro (usa getEffectiveDate)
          const dateA2 = getEffectiveDate(a);
          const dateB2 = getEffectiveDate(b);
          return dateA2.getTime() - dateB2.getTime();
        
        case 'amount-desc':
          // Maior valor primeiro
          return Number(b.amount) - Number(a.amount);
        
        case 'amount-asc':
          // Menor valor primeiro
          return Number(a.amount) - Number(b.amount);
        
        case 'description-asc':
          // A-Z
          return a.description.localeCompare(b.description, 'pt-BR');
        
        case 'description-desc':
          // Z-A
          return b.description.localeCompare(a.description, 'pt-BR');
        
        default:
          return 0;
      }
    });
  }, [transactions, filters, sortBy, showContent]);

  // Visible transactions (pagination)
  const visibleTransactions = useMemo(() => {
    return filteredTransactions.slice(0, visibleCount);
  }, [filteredTransactions, visibleCount]);

  // PERFORMANCE: Defer content rendering to prevent blocking
  useEffect(() => {
    if (!isLoading && transactions.length > 0) {
      // Reset showContent when navigating
      setShowContent(false);
      
      // Use requestIdleCallback for best performance, fallback to setTimeout
      const callback = () => {
        setShowContent(true);
      };

      if ('requestIdleCallback' in window) {
        const id = requestIdleCallback(callback, { timeout: 50 });
        return () => cancelIdleCallback(id);
      } else {
        const id = setTimeout(callback, 0);
        return () => clearTimeout(id);
      }
    } else if (!isLoading && transactions.length === 0) {
      // Empty state - show immediately
      setShowContent(true);
    }
  }, [isLoading, transactions.length]);

  // Limpar seleção quando os filtros mudarem
  useEffect(() => {
    setSelectedIds(new Set());
    setSelectionMode(false);
  }, [filters, transactions.length]);

  // Desativar modo de seleção quando não há mais itens selecionados
  useEffect(() => {
    if (selectedIds.size === 0 && selectionMode) {
      setSelectionMode(false);
    }
  }, [selectedIds.size, selectionMode]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl/Cmd + N = New Transaction
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        e.preventDefault();
        onNavigateToForm();
      }

      // Ctrl/Cmd + F = Focus search
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        document.querySelector<HTMLInputElement>('[placeholder*="Buscar"]')?.focus();
      }

      // ESC = Limpar seleção
      if (e.key === 'Escape' && selectionMode) {
        e.preventDefault();
        setSelectedIds(new Set());
        setSelectionMode(false);
        toast.info('Seleção cancelada');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onNavigateToForm, selectionMode]);

  const handleEdit = (transaction: CashFlowEntry) => {
    // Não mostrar mais aviso aqui - o aviso será no formulário
    
    // Se é lançamento recorrente, mostrar dialog de escolha ANTES de abrir o formulário
    if (transaction.is_recurring && transaction.recurrence_group_id) {
      setRecurringEditDialog({ open: true, transaction });
      return;
    }
    
    // Lançamento normal, abrir formulário diretamente
    onNavigateToForm(transaction.id);
  };

  const handleCreate = () => {
    onNavigateToForm();
  };

  const handleRecurring = () => {
    onNavigateToForm(undefined, { mode: 'recurring' });
  };

  const handleDuplicate = (transaction?: CashFlowEntry) => {
    // Se transaction for passado, duplicar esse lançamento específico
    // Senão, duplicar o último lançamento (primeiro da lista ordenada por data desc)
    const transactionToDuplicate = transaction || transactions[0];
    
    if (!transactionToDuplicate) {
      toast.info('Nenhum lançamento disponível para duplicar');
      return;
    }
    
    console.log('[TransactionsPage] Duplicating transaction:', {
      id: transactionToDuplicate.id,
      description: transactionToDuplicate.description,
      amount: transactionToDuplicate.amount,
    });
    
    onNavigateToForm(undefined, { 
      mode: 'duplicate', 
      duplicateFromId: transactionToDuplicate.id 
    });
  };

  const handleRecurringEdit = (option: EditOption) => {
    if (!recurringEditDialog.transaction) return;
    
    // Salvar a opção escolhida no localStorage para a página de edição usar
    localStorage.setItem(
      `transaction_edit_option_${recurringEditDialog.transaction.id}`,
      option
    );
    
    // Fechar o dialog
    setRecurringEditDialog({ open: false, transaction: null });
    
    // Navegar para o formulário de edição
    onNavigateToForm(recurringEditDialog.transaction.id);
  };

  // Calculate series info for recurring transactions
  const getSeriesInfo = (transaction: CashFlowEntry) => {
    if (!transaction.recurrence_group_id) {
      return { total: 0, future: 0, totalAmount: 0, futureAmount: 0 };
    }

    // Sort by entry_date to ensure correct ordering
    const seriesTransactions = transactions
      .filter(t => t.recurrence_group_id === transaction.recurrence_group_id)
      .sort((a, b) => (a.entry_date || '').localeCompare(b.entry_date || ''));

    const futureTransactions = seriesTransactions.filter(
      t => t.entry_date && transaction.entry_date && t.entry_date >= transaction.entry_date
    );

    return {
      total: seriesTransactions.length,
      future: futureTransactions.length,
      totalAmount: seriesTransactions.reduce((sum, t) => sum + t.amount, 0),
      futureAmount: futureTransactions.reduce((sum, t) => sum + t.amount, 0),
    };
  };

  const handleDelete = async (id: string) => {
    const transaction = transactions.find(t => t.id === id);
    
    // If recurring transaction, show dialog with options
    if (transaction?.is_recurring && transaction?.recurrence_group_id) {
      setRecurringDeleteDialog({ open: true, transaction });
      setDeletingId(null); // Close confirm dialog
      return;
    }

    // Regular delete for non-recurring
    try {
      await deleteTransaction(id);
      setDeletingId(null);
      toast.success('Lançamento excluído com sucesso');
    } catch (error) {
      toast.error('Erro ao excluir lançamento');
    }
  };

  const handleRecurringDelete = async (option: DeleteOption) => {
    if (!recurringDeleteDialog.transaction) return;

    try {
      await deleteRecurringTransaction({
        transaction: recurringDeleteDialog.transaction,
        option,
      });
      setRecurringDeleteDialog({ open: false, transaction: null });
    } catch (error) {
      console.error('Error deleting recurring transaction:', error);
    }
  };

  // Função para alternar seleção de um item
  const handleToggleSelect = (id: string) => {
    // Ativar modo de seleção ao clicar em qualquer checkbox
    if (!selectionMode) {
      setSelectionMode(true);
    }
    
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Função para selecionar/deselecionar todos
  const handleToggleSelectAll = () => {
    if (selectedIds.size === visibleTransactions.length && visibleTransactions.length > 0) {
      // Se todos estão selecionados, desselecionar todos
      setSelectedIds(new Set());
    } else {
      // Selecionar todos os visíveis e ativar modo de seleção
      setSelectionMode(true);
      setSelectedIds(new Set(visibleTransactions.map(t => t.id)));
    }
  };

  // Função para iniciar exclusão múltipla
  const handleDeleteSelectedClick = () => {
    if (selectedIds.size === 0) return;

    // Mostrar dialog de confirmação
    setShowBulkDeleteDialog(true);
  };

  // Função para confirmar exclusão múltipla
  const handleConfirmBulkDelete = async () => {
    if (selectedIds.size === 0) return;

    const selectedTransactions = transactions.filter(t => selectedIds.has(t.id));
    
    // Excluir todos os selecionados
    let successCount = 0;
    let errorCount = 0;

    for (const transaction of selectedTransactions) {
      try {
        // Se for lançamento recorrente, excluir apenas este (opção 'single')
        if (transaction.is_recurring && transaction.recurrence_group_id) {
          await deleteRecurringTransaction({
            transaction,
            option: 'single', // Sempre exclui apenas o lançamento específico
          });
        } else {
          // Lançamento normal
          await deleteTransaction(transaction.id);
        }
        successCount++;
      } catch (error) {
        console.error(`Error deleting transaction ${transaction.id}:`, error);
        errorCount++;
      }
    }

    // Limpar seleção
    setSelectedIds(new Set());
    setShowBulkDeleteDialog(false);

    // Mostrar resultado
    if (errorCount === 0) {
      toast.success(`${successCount} lançamento(s) excluído(s) com sucesso!`);
    } else {
      toast.warning(`${successCount} excluído(s), ${errorCount} com erro.`);
    }
  };

  const handleDuplicateFromList = (transaction: CashFlowEntry) => {
    console.log('[TransactionsPage] Duplicating from list:', {
      id: transaction.id,
      description: transaction.description,
      amount: transaction.amount,
      entry_date: transaction.entry_date,
    });
    
    onNavigateToForm(undefined, { 
      mode: 'duplicate', 
      duplicateFromId: transaction.id 
    });
  };

  const handleView = (transaction: CashFlowEntry) => {
    toast.info('Preview será implementado em breve');
    // TODO: Implement preview drawer
  };

  const handleExport = () => {
    setExportDialogOpen(true);
  };

  const handleImport = () => {
    setImportDialogOpen(true);
  };

  const handleLoadMore = () => {
    setVisibleCount(prev => prev + 20);
  };

  // ALWAYS show skeleton during initial load or when content not ready
  if (isLoading || !showContent) {
    return <TransactionListSkeleton />;
  }

  // Error state
  if (isError) {
    return (
      <Container size="large" className="py-6 md:py-8">
        <Stack spacing="large">
          <div className="text-center py-12">
            <h2 className="text-red-500">Erro ao carregar lançamentos</h2>
            <p className="text-muted-foreground mt-2">
              {error instanceof Error ? error.message : 'Erro desconhecido'}
            </p>
            <Button onClick={() => window.location.reload()} className="mt-4">
              Tentar Novamente
            </Button>
          </div>
        </Stack>
      </Container>
    );
  }

  return (
    <Container size="large" className="py-6 md:py-8">
      <Stack spacing="large">
        {/* Header with Actions */}
        <PageHeader
          icon={Receipt}
          title="Lançamentos"
          description={`${filteredTransactions.length} de ${transactions.length} lançamentos`}
          actions={
            <div className="flex gap-2 flex-wrap">
              <PermissionGuard check="canEdit" module="transactions">
                <Sheet open={recurringManagerOpen} onOpenChange={setRecurringManagerOpen}>
                  <SheetTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="lg"
                    >
                      <Repeat className="w-4 h-4 mr-2" />
                      Recorrências
                    </Button>
                  </SheetTrigger>
                  <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
                    <SheetHeader className="mb-6">
                      <SheetTitle className="flex items-center gap-2">
                        <Repeat className="h-5 w-5" />
                        Gerenciar Recorrências
                      </SheetTitle>
                      <SheetDescription>
                        Visualize e gerencie todos os lançamentos recorrentes
                      </SheetDescription>
                    </SheetHeader>
                    <RecurringTransactionsManager
                      companyId={typeof selectedCompanyIds === 'string' ? selectedCompanyIds : selectedCompanyIds[0]}
                      userId={userId}
                      onEdit={(transaction) => {
                        setRecurringManagerOpen(false);
                        onNavigateToForm(transaction.id);
                      }}
                    />
                  </SheetContent>
                </Sheet>
              </PermissionGuard>
              
              <PermissionGuard check="canCreate" module="transactions">
                <Button 
                  variant="outline" 
                  size="lg"
                  onClick={handleImport}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Importar
                </Button>
              </PermissionGuard>

              <Button 
                variant="outline" 
                size="lg"
                onClick={handleExport}
                disabled={transactions.length === 0}
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </Button>
            </div>
          }
        />

        {/* Selected Companies Indicator */}
        <SelectedCompaniesIndicator />

        {/* Quick Actions - Only for users with create permission */}
        <PermissionGuard check="canCreate" module="transactions">
          <TransactionQuickActions
            onNewTransaction={handleCreate}
            onDuplicate={handleDuplicate}
            onRecurring={handleRecurring}
          />
        </PermissionGuard>

        {/* Statistics */}
        {transactions.length > 0 && (
          <TransactionStats transactions={filteredTransactions} />
        )}

        {/* Filters */}
        {transactions.length > 0 && (
          <Card className="p-4">
            <TransactionFilters
              filters={filters}
              onFiltersChange={setFilters}
              categories={categories}
              bankAccounts={bankAccounts}
            />
          </Card>
        )}

        {/* Transactions List */}
        <Card className="p-6">
            {filteredTransactions.length === 0 ? (
              <TransactionEmptyState
                onCreateTransaction={handleCreate}
                hasFilters={transactions.length > 0}
                onClearFilters={() => setFilters({
                  search: '',
                  entryType: 'all',
                  paymentStatus: 'all',
                })}
              />
              ) : (
              <div className="space-y-3">
                {/* Barra de Seleção e Ordenação - acima da lista */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pb-3 border-b">
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="flex items-center gap-2 cursor-pointer text-sm font-medium">
                      <input
                        type="checkbox"
                        checked={selectedIds.size === visibleTransactions.length && visibleTransactions.length > 0}
                        onChange={handleToggleSelectAll}
                        className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                      />
                      {selectedIds.size === visibleTransactions.length && visibleTransactions.length > 0
                        ? 'Desselecionar Todos'
                        : 'Selecionar Todos'}
                    </label>

                    {/* Ordenação */}
                    {selectedIds.size === 0 && (
                      <div className="flex items-center gap-2">
                        <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
                        <Select value={sortBy} onValueChange={(value) => setSortBy(value as SortOption)}>
                          <SelectTrigger className="w-[180px] h-9 text-sm">
                            <SelectValue placeholder="Ordenar por..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="date-desc">Data (mais recente)</SelectItem>
                            <SelectItem value="date-asc">Data (mais antiga)</SelectItem>
                            <SelectItem value="amount-desc">Valor (maior)</SelectItem>
                            <SelectItem value="amount-asc">Valor (menor)</SelectItem>
                            <SelectItem value="description-asc">Descrição (A-Z)</SelectItem>
                            <SelectItem value="description-desc">Descrição (Z-A)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  {/* Ações quando há itens selecionados */}
                  {selectedIds.size > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">
                        {selectedIds.size} selecionado(s)
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedIds(new Set());
                          setSelectionMode(false);
                        }}
                      >
                        Cancelar
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleDeleteSelectedClick}
                        disabled={isDeleting}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Excluir
                      </Button>
                    </div>
                  )}
                </div>

                {visibleTransactions.map((transaction) => {
                  const company = selectedCompanies.find(c => c.id === transaction.company_id);
                  return (
                    <TransactionListItem
                      key={transaction.id}
                      transaction={transaction}
                      onEdit={handleEdit}
                      onDelete={setDeletingId}
                      onDuplicate={handleDuplicateFromList}
                      isSelected={selectedIds.has(transaction.id)}
                      onToggleSelect={handleToggleSelect}
                      isMobile={isMobile}
                      companyName={company?.name}
                      showCompany={hasMultipleSelected}
                      canEdit={canEdit('transactions')}
                      isReconciled={reconciledIds.has(transaction.id)}
                    />
                  );
                })}

                {/* Load More Button */}
                {visibleCount < filteredTransactions.length && (
                  <div className="text-center py-6 border-t">
                    <p className="text-sm text-muted-foreground mb-3">
                      Mostrando {visibleCount} de {filteredTransactions.length} lançamentos
                    </p>
                    <Button 
                      variant="outline" 
                      onClick={handleLoadMore}
                    >
                      Carregar mais 20
                    </Button>
                  </div>
                )}

                {/* All loaded */}
                {visibleCount >= filteredTransactions.length && filteredTransactions.length > 20 && (
                  <div className="text-center py-6 border-t">
                    <p className="text-sm text-muted-foreground">
                      Todos os {filteredTransactions.length} lançamentos carregados
                    </p>
                  </div>
                )}
              </div>
            )}
          </Card>

        {/* Tips */}
        {transactions.length > 0 && (
          <Card className="p-4 bg-muted/50">
              <div className="flex items-start gap-3">
                <Zap className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium">Dicas de Produtividade</p>
                  <p className="text-xs text-muted-foreground">
                    Use <kbd className="px-1.5 py-0.5 bg-background border rounded text-[10px] font-mono">Ctrl+N</kbd> para novo lançamento
                    {' • '}
                    <kbd className="px-1.5 py-0.5 bg-background border rounded text-[10px] font-mono">Ctrl+F</kbd> para buscar
                    {' • '}
                    <kbd className="px-1.5 py-0.5 bg-background border rounded text-[10px] font-mono">ESC</kbd> para limpar seleção
                    {' • '}
                    Passe o mouse sobre os itens para ver mais opções
                  </p>
                </div>
              </div>
            </Card>
        )}
      </Stack>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingId} onOpenChange={() => setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingId && reconciledIds.has(deletingId) ? (
                <>
                  <span className="text-warning-600 font-semibold block mb-2">
                    ⚠️ ATENÇÃO: Este lançamento está conciliado!
                  </span>
                  <span>
                    Ao excluir este lançamento, a conciliação bancária será REMOVIDA automaticamente. 
                    Tem certeza que deseja prosseguir? Esta ação não pode ser desfeita.
                  </span>
                </>
              ) : (
                'Tem certeza que deseja excluir este lançamento? Esta ação não pode ser desfeita.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingId && handleDelete(deletingId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Recurring Dialog */}
      {recurringDeleteDialog.transaction && (
        <DeleteRecurrenceDialog
          open={recurringDeleteDialog.open}
          onOpenChange={(open) => setRecurringDeleteDialog({ open, transaction: null })}
          transaction={recurringDeleteDialog.transaction}
          seriesCount={{
            total: getSeriesInfo(recurringDeleteDialog.transaction).total,
            future: getSeriesInfo(recurringDeleteDialog.transaction).future,
          }}
          seriesTotals={{
            total: getSeriesInfo(recurringDeleteDialog.transaction).totalAmount,
            future: getSeriesInfo(recurringDeleteDialog.transaction).futureAmount,
          }}
          onConfirm={handleRecurringDelete}
          isDeleting={isDeleting}
        />
      )}

      {/* Edit Recurring Dialog */}
      {recurringEditDialog.transaction && (
        <EditRecurrenceDialog
          open={recurringEditDialog.open}
          onOpenChange={(open) => setRecurringEditDialog({ open, transaction: null })}
          transaction={recurringEditDialog.transaction}
          seriesCount={{
            total: getSeriesInfo(recurringEditDialog.transaction).total,
            future: getSeriesInfo(recurringEditDialog.transaction).future,
          }}
          onConfirm={handleRecurringEdit}
          isUpdating={false}
        />
      )}

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão múltipla</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir {selectedIds.size} lançamento(s)? 
              {transactions.filter(t => selectedIds.has(t.id)).some(t => t.is_recurring) && (
                <span className="block mt-2 text-amber-600 dark:text-amber-500 font-medium">
                  ATENÇÃO: Lançamentos recorrentes serão excluídos individualmente (não afeta outros da série).
                </span>
              )}
              <span className="block mt-2">Esta ação não pode ser desfeita.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmBulkDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir {selectedIds.size} lançamento(s)
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Export Dialog */}
      <ExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        companyId={typeof selectedCompanyIds === 'string' ? selectedCompanyIds : selectedCompanyIds[0]}
        companyName={selectedCompanies[0]?.name || 'Empresa'}
        transactionCount={transactions.length}
      />

      {/* Import Dialog */}
      <ImportDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        companyId={typeof selectedCompanyIds === 'string' ? selectedCompanyIds : selectedCompanyIds[0]}
        userId={userId}
      />
    </Container>
  );
}
